import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getMilestoneDetails } from '../../services/memoryCategoriesService';
import { toast } from 'sonner';

/**
 * MilestoneMatchCard Component
 * 
 * Displays matched ASQ milestone information with similarity score and age-appropriateness.
 */
const MilestoneMatchCard = ({ milestone, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [details, setDetails] = useState(null);

  const handleViewDetails = async () => {
    if (details) {
      if (onViewDetails) {
        onViewDetails(milestone.milestone_id, details);
      }
      return;
    }

    setLoadingDetails(true);
    try {
      const milestoneDetails = await getMilestoneDetails(milestone.milestone_id);
      setDetails(milestoneDetails);
      if (onViewDetails) {
        onViewDetails(milestone.milestone_id, milestoneDetails);
      }
    } catch (error) {
      toast.error('Failed to load milestone details');
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const similarityColor = milestone.similarity_score >= 0.8 
    ? 'bg-green-100 text-green-800' 
    : milestone.similarity_score >= 0.6 
    ? 'bg-yellow-100 text-yellow-800' 
    : 'bg-gray-100 text-gray-800';

  return (
    <Card className="mb-2">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-sm font-semibold">
              {milestone.milestone_name}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              ID: {milestone.milestone_id} • {milestone.domain}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn('text-xs', similarityColor)}>
              {(milestone.similarity_score * 100).toFixed(0)}% match
            </Badge>
            {milestone.age_appropriate ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" title="Age-appropriate" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" title="Not age-appropriate" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-gray-700 mb-2">
          {milestone.milestone_question}
        </p>
        {milestone.expected_age_range && (
          <p className="text-xs text-gray-500 mb-2">
            Expected age: {milestone.expected_age_range}
            {milestone.child_age_months && ` • Child age: ${milestone.child_age_months} months`}
          </p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDetails}
            disabled={loadingDetails}
            className="text-xs"
          >
            {loadingDetails ? 'Loading...' : 'View Details'}
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
          {milestone.explanation_primary && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-xs"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          )}
        </div>
        {expanded && milestone.explanation_primary && (
          <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
            <p className="font-medium mb-1">Explanation:</p>
            <p>{milestone.explanation_primary}</p>
            {milestone.explanation_secondary && (
              <p className="mt-1">{milestone.explanation_secondary}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MilestoneMatchCard;

